import {Node} from "../Node/Node";
import MinPQ from "./MinPQ";
import {Comparable} from "./Comparable";

class BestFirstNode implements Comparable<BestFirstNode> {
  node: Node;
  prev: BestFirstNode | undefined;
  readonly moves: number;
  readonly manhattan: number;

  constructor(node: Node, goal: Node, prev: BestFirstNode | undefined) {
    this.node = node;
    this.prev = prev;
    this.manhattan = manhattanDistance(node, goal);
    if (prev !== undefined) {
      if (node.isWeight) this.moves = prev.moves + 5;
      else this.moves = prev.moves + 1;
    } else {
      this.moves = 0;
    }
  }

  compareTo(o: BestFirstNode): number {
    let priorityDiff = (this.manhattan - o.manhattan);
    if (priorityDiff === 0) {
      return this.manhattan - o.manhattan;
    } else {
      return priorityDiff;
    }
  }
}

export function bestFirstSearch(s: Node, t: Node, grid: Node[][]) {
  let pathNodes: Node[] = [];
  let visitedNodes: Node[] = [];

  let pq = new MinPQ<BestFirstNode>();
  pq.insert(new BestFirstNode(s, t, undefined));

  while (pq.isEmpty() || !pq.min()!.node.isFinish) {
    if (pq.isEmpty()) {
      return {visitedNodes: visitedNodes, pathNodes: pathNodes};
    }

    let minNode: BestFirstNode = pq.delMin() as BestFirstNode;
    if (minNode.node.isVisited) continue;

    visitedNodes.push(minNode.node);
    minNode.node.isVisited = true;
    for (let node of neighbors(minNode.node, grid)) {
      if (node.isWall) continue;
      if (minNode.prev === undefined || minNode.prev.node !== node) {
        pq.insert(new BestFirstNode(node, t, minNode));
      }
    }
  }

  let current = pq.min();
  visitedNodes.push(current!.node);

  while (current?.prev !== undefined) {
    pathNodes.unshift(current!.node);
    current = current.prev;
  }

  pathNodes.unshift(current!.node);

  return {visitedNodes, pathNodes};
}

function neighbors(s: Node, grid: Node[][]) {
  const {row, col} = s;
  let neighbors: Node[] = [];

  if (col > 0) neighbors.push(grid[row][col - 1]);
  if (col < grid[0].length - 1) neighbors.push(grid[row][col + 1]);
  if (row > 0) neighbors.push(grid[row - 1][col]);
  if (row < grid.length - 1) neighbors.push(grid[row + 1][col]);

  return neighbors.filter(neighbor => !neighbor.isVisited && !neighbor.isWall);
}

function manhattanDistance(s: Node, t: Node) {
  let weight = s.isWeight ? 5 : 1;
  return Math.abs(s.row - t.row) + Math.abs(s.col - t.col) + weight;
}

